import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Table, Button, OverlayTrigger, Tooltip, Badge } from 'react-bootstrap';
import { FaPlus, FaEdit, FaSearch, FaUndo, FaEye, FaTrash, FaToggleOn, FaToggleOff, FaCopy } from "react-icons/fa";
import APIServicenew from "../../utils/APIGeneralService";
import { UserContext } from "../../context/UserContextProvider";
import { ToastContainer, toast } from "react-toastify";
import CourseFilter from "./CourseFilter";
import useMenuPermissions from "../../components/CustomHook/custompermission";
import { capitalizeFirstLetter, formateDatemdy } from "../../utils/Helper";
import Paginator from "../../components/common/Pagination";
import DeleteModal from "../../components/common/DeleteModel";

const ManageCourse = () => {
  const permissionscontext = useMenuPermissions("/admin/course");
  const [courseList, setCourseList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [count, setCount] = useState(0);
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showDt, setShowDt] = useState(false);
  const [updatingPublish, setUpdatingPublish] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState(null);

  const navigate = useNavigate();
  const limit = 10;

  const APIServices = new APIServicenew(process.env.REACT_APP_OTHER_API_URL);
  const authToken = process.env.REACT_APP_authToken;
  const { accessToken, logout } = useContext(UserContext);

  const headers = {
    "auth-token": authToken,
    "authorization": accessToken
  };

  useEffect(() => {
    fetchCourses();
  }, [currentPage, filters, searchTerm]);

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const params = {
        skip: (currentPage - 1) * limit,
        limit: limit,
      };

      if (searchTerm && searchTerm.trim() !== "") {
        params.search = searchTerm.trim();
      }

      if (filters.search && filters.search.trim() !== "") {
        params.search = filters.search.trim();
      }

      if (filters.language && filters.language !== "") {
        params.language = filters.language;
      }

      if (filters.status && filters.status !== "") {
        params.status = filters.status;
      }

      if (filters.published_status && filters.published_status !== "") {
        params.published_status = filters.published_status;
      }

      const queryString = new URLSearchParams(params).toString();

      const response = await APIServices.get(
        `/admin/courses/list?${queryString}`,
        headers
      );

      if (response?.data?.status === true) {
        setCourseList(response?.data?.data?.data || []);
        const total = response?.data?.data?.total || 0;
        setCount(total);
        setTotalCount(Math.ceil(total / limit) || 0);
      } else if (response?.status === 401) {
        logout();
        navigate("/");
      } else {
        setCourseList([]);
        setCount(0);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error fetching courses:", error);
      showToast("Error fetching courses", "error");
      setCourseList([]);
      setCount(0);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Update Status using FormData
  const handleStatusChange = async (course) => {
    const newStatus = course.status === "Active" ? "Inactive" : "Active";
    
    const formData = new FormData();
    formData.append("id", course.id);
    formData.append("status", newStatus);
    
    try {
      const res = await APIServices.put(
        `/admin/courses/status`,
        formData,
        {
          ...headers,
          'Content-Type': 'multipart/form-data'
        }
      );

      if (res?.data?.status === true) {
        showToast(res?.data?.message || "Status updated successfully", "success");
        fetchCourses();
      } else {
        showToast(res?.data?.message || "Something went wrong.", "error");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      showToast("Error updating status", "error");
    }
  };

  // Update Publish Status using FormData
  const handlePublishChange = async (course) => {
    const newPublishStatus = course.published_status === "Published" ? "Draft" : "Published";
    
    const formData = new FormData();
    formData.append("id", course.id);
    formData.append("status", newPublishStatus);
    
    setUpdatingPublish(true);
    try {
      const res = await APIServices.put(
        `/admin/courses/publish`,
        formData,
        {
          ...headers,
          'Content-Type': 'multipart/form-data'
        }
      );

      if (res?.data?.status === true) {
        showToast(res?.data?.message || "Publish status updated successfully", "success");
        fetchCourses();
      } else {
        showToast(res?.data?.message || "Something went wrong.", "error");
      }
    } catch (error) {
      console.error("Error updating publish status:", error);
      showToast("Error updating publish status", "error");
    } finally {
      setUpdatingPublish(false);
    }
  };

  const handleDuplicateCourse = async (courseId) => {
    setDuplicatingId(courseId);
    try {
      const formData = new FormData();
      formData.append("id", courseId);

      const res = await APIServices.post(
        "/admin/courses/duplicate",
        formData,
        {
          ...headers,
          'Content-Type': 'multipart/form-data'
        }
      );

      if (res?.data?.status === true) {
        showToast(res?.data?.message || "Course duplicated successfully", "success");
        fetchCourses();
      } else {
        showToast(res?.data?.message || "Something went wrong.", "error");
      }
    } catch (error) {
      console.error("Error duplicating course:", error);
      showToast("Error duplicating course", "error");
    } finally {
      setDuplicatingId(null);
    }
  };

  // Delete Course using FormData
  const handleDeleteCourse = async () => {
    try {
      if (selectedCourse !== null) {
        const formData = new FormData();
        formData.append("id", selectedCourse);
        
        const res = await APIServices.delete(
          `/admin/courses/delete`,
          formData,
          {
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        );

        if (res?.data?.status === true) {
          showToast(res?.data?.message || "Course deleted successfully", "success");
          if (courseList.length === 1 && currentPage > 1) {
            setCurrentPage(currentPage - 1);
          } else {
            fetchCourses();
          }
          setSelectedCourse(null);
        } else {
          showToast(res?.data?.message || "Something went wrong.", "error");
        }
      }
      setShowDt(false);
    } catch (error) {
      console.error("Error deleting course:", error);
      showToast("Error deleting course", "error");
      setShowDt(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearch = () => {
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleApplyFilter = (filterData) => {
    setFilters(filterData);
    setCurrentPage(1);
    setShowFilter(false);
  };

  const handleClearFilter = () => {
    setFilters({});
    setCurrentPage(1);
    setShowFilter(false);
  };

  const showToast = (message, type) => {
    toast(message, {
      position: "top-right",
      autoClose: 2000,
      type,
      hideProgressBar: false,
      closeOnClick: true,
    });
  };

  const getSerialNumber = (index) => {
    return (currentPage - 1) * limit + index + 1;
  };

  const getPublishedBadge = (published) => {
    switch (published) {
      case 'Published':
        return <Badge bg="success">Published</Badge>;
      case 'Draft':
        return <Badge bg="warning">Draft</Badge>;
      default:
        return <Badge bg="secondary">{published}</Badge>;
    }
  };

  const hasActiveFilters = Object.keys(filters).some(key => filters[key] && filters[key] !== "");

  const getFilterDisplayText = () => {
    const activeFilters = [];
    if (filters.search) activeFilters.push(`search: ${filters.search}`);
    if (filters.language) activeFilters.push(`Language: ${filters.language}`);
    if (filters.status) activeFilters.push(`Status: ${filters.status}`);
    if (filters.published_status) activeFilters.push(`Published: ${filters.published_status}`);
    return activeFilters.join(", ");
  };

  return (
    <>
      <div className="momList_inner todo_list_inner course_list_inner">
        <div className="mom_header d-flex align-items-center justify-content-between mb-3">
          <h1 className="font_blue_18 font-600">
            {`Course Management (${count})`}
          </h1>

          <ul className="d-flex align-items-center mom_filter">
            <li
              className="d-flex align-items-center cursor-pointer"
              onClick={() => setShowFilter(true)}
            >
              Filter By{" "}
              <img
                src="/images/Sort.svg"
                className="img-fluid ms-2"
                alt="Sort"
              />
            </li>
            <li>
              {permissionscontext.add == 1 && (
                <button
                  className="btn red_button d-flex align-items-center"
                  onClick={() => navigate("/admin/add-course")}
                >
                  <FaPlus className="me-1" />
                  Add Course
                </button>
              )}
            </li>
          </ul>
        </div>
        <div className="todoList_table">
          <div className="table-responsive">
            <Table className="table">
              <thead className="table-dark">
                <tr>
                  <th style={{ width: '3%' }}>#</th>
                  <th style={{ width: '10%' }}>Image</th>
                  <th style={{ width: '8%' }}>Course ID</th>
                  <th style={{ width: '15%' }}>Title</th>
                  <th style={{ width: '8%' }}>Duration</th>
                  <th style={{ width: '8%' }}>Language</th>
                  <th style={{ width: '10%' }}>No of Grades</th>
                  <th style={{ width: '10%' }}>No of Student</th>
                  <th style={{ width: '8%' }}>Status</th>
                  <th style={{ width: '10%' }}>Published</th>
                  <th style={{ width: '10%' }}>Created Date</th>
                  <th style={{ width: '10%' }}>Expiry Date</th>
                  <th style={{ width: '10%' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="11" className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : courseList.length > 0 ? (
                  courseList.map((course, index) => (
                    <tr key={course.id}>
                      <td>{getSerialNumber(index)}</td>
                     
                      <td>
                        {course.image ? (
                          <img
                            src={course.image}
                            alt={course.title}
                            style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '5px' }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = '/images/no-image.png';
                            }}
                          />
                        ) : (
                          <img
                            src="/images/no-image.png"
                            alt="No Image"
                            style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '5px' }}
                          />
                        )}
                      </td>
                       <td>
                        {course.unique_course_id}
                      </td>
                      <td>
                        <strong>{capitalizeFirstLetter(course.title)}</strong>
                      </td>
                      <td>{course.duration}</td>
                      <td>{course.language}</td>
                      <td>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip>
                              {course.grade_name?.join(', ')}
                            </Tooltip>
                          }
                        >
                          <>
                            {course.grade_name?.length || 0}
                          </>
                        </OverlayTrigger>
                      </td>
                      <td>{course.total_students}</td>
                      <td>
                        <Form className="Switch_toggle">
                          <input
                            type="checkbox"
                            id={`switch-${course.id}`}
                            checked={course.status === "Active"}
                            onChange={() => handleStatusChange(course)}
                            disabled={!permissionscontext.edit}
                          />
                          <label
                            className="position-relative"
                            htmlFor={`switch-${course.id}`}
                          >
                            Toggle
                          </label>
                        </Form>
                      </td>
                      <td>
                        <Button
                          variant={course.published_status === "Published" ? "success" : "warning"}
                          size="sm"
                          onClick={() => handlePublishChange(course)}
                          disabled={!permissionscontext.edit}                          
                          className="d-flex align-items-center gap-1"
                        >
                          {course.published_status === "Published" ? (
                            <FaToggleOn size={25} className="position-relative"/> 
                          ) : (
                            <FaToggleOff size={25} className="position-relative"/>
                          )}
                          {course.published_status}
                        </Button>
                      </td>
                    
                      <td>{formateDatemdy(course.created_at)}</td>
                      <td>
                        {course.expiry_date && new Date(course.expiry_date) < new Date() ? (
                          <Badge bg="danger">Expired</Badge>
                        ) : (
                          formateDatemdy(course.expiry_date)
                        )}
                      </td>
                      <td>
                        <ul className="d-flex align-items-center list-unstyled mb-0">
                          <li className="me-2">
                            <OverlayTrigger placement="top" overlay={<Tooltip>View Details</Tooltip>}>
                              <button
                                className="btn btn-sm btn-outline-info py-1"
                                onClick={() => {
                                  navigate(`/admin/course-detail/${course.id}`, {
                                    state: { row: course },
                                  });
                                }}
                              >
                                <FaEye />
                              </button>
                            </OverlayTrigger>
                          </li>
                          <li className="me-2">
                            {permissionscontext.add == 1 && (
                              <OverlayTrigger placement="top" overlay={<Tooltip>Duplicate Course</Tooltip>}>
                                <button
                                  className="btn btn-sm btn-outline-warning py-1"
                                  onClick={() => handleDuplicateCourse(course.id)}
                                  disabled={duplicatingId === course.id}
                                >
                                  <FaCopy />
                                </button>
                              </OverlayTrigger>
                            )}
                          </li>
                          <li className="me-2">
                            {permissionscontext.edit == 1 && (
                              <OverlayTrigger placement="top" overlay={<Tooltip>Edit Course</Tooltip>}>
                                <button
                                  className="btn btn-sm btn-outline-success py-1"
                                  onClick={() => {
                                    navigate(`/admin/update-course/${course.id}`, {
                                      state: { row: course },
                                    });
                                  }}
                                >
                                  <FaEdit />
                                </button>
                              </OverlayTrigger>
                            )}
                          </li>
                          <li>
                            {permissionscontext.delete == 1 && (
                              <OverlayTrigger placement="top" overlay={<Tooltip>Delete Course</Tooltip>}>
                                <button
                                  className="btn btn-sm btn-outline-danger py-1"
                                  onClick={() => {
                                    setSelectedCourse(course.id);
                                    setShowDt(true);
                                  }}
                                >
                                  <FaTrash />
                                </button>
                              </OverlayTrigger>
                            )}
                          </li>
                        </ul>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="11" className="text-center py-4">
                      <div className="text-muted">
                        <h5>No Courses found</h5>
                        <p>
                          {hasActiveFilters || searchTerm
                            ? "Try clearing filters or search terms"
                            : "Click on 'Add Course' to create your first course"}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>

        <div className="pagination_box mt-4">
          {count > limit && (
            <Paginator
              currentPage={currentPage}
              totalPages={totalCount}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </div>

      {/* Filter Offcanvas */}
      {showFilter && (
        <CourseFilter
          appliedFilters={filters}
          show={showFilter}
          onHide={() => setShowFilter(false)}
          onApplyFilter={handleApplyFilter}
          onClear={handleClearFilter}
        />
      )}

      {/* Delete Modal */}
      <DeleteModal
        showdt={showDt}
        handleCloseDt={() => setShowDt(false)}
        handledelete={handleDeleteCourse}
      />

      <ToastContainer />
    </>
  );
};

export default ManageCourse;